import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const STATUSES = ['OPEN', 'INVESTIGATING', 'PENDING_INFORMATION', 'COMPLETED', 'CLOSED'];
const FINDINGS = ['CONFIRMED', 'PARTIALLY_CONFIRMED', 'NOT_CONFIRMED', 'INCONCLUSIVE'];
const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

function formatMs(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export const CallQaInvestigationsPage: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('call_qa.investigations.read');
  const canManage = permissions.includes('call_qa.investigations.manage');
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [desc, setDesc] = useState('');
  const [customer, setCustomer] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['callQaInvestigations', status, q],
    queryFn: () => apiClient.callQa.listInvestigations({
      status: status || undefined,
      q: q || undefined,
    }),
    enabled: canRead,
  });

  const createMutation = useMutation({
    mutationFn: () => apiClient.callQa.createInvestigation({
      complaint_description: desc,
      customer_name: customer || null,
      source: 'MANUAL',
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['callQaInvestigations'] });
      const id = res.data?.data?.id;
      if (id) navigate(`/call-qa/investigations/${id}`);
    },
  });

  if (!canRead) {
    return <Alert severity="warning">Investigations require call_qa.investigations.read.</Alert>;
  }

  const rows = data?.data?.data?.rows || [];

  return (
    <Box>
      <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          label="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="investigation-search"
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="investigation-status-filter"
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        {canManage && (
          <Button variant="contained" size="small" onClick={() => setCreateOpen((v) => !v)}>
            New Investigation
          </Button>
        )}
      </Box>

      {createOpen && canManage && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="investigation-create-form">
          <Typography variant="subtitle2" gutterBottom>Create Investigation</Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Customer" value={customer} onChange={(e) => setCustomer(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Complaint description"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                required
              />
            </Grid>
          </Grid>
          <Button
            sx={{ mt: 1 }}
            size="small"
            variant="contained"
            disabled={!desc.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Create
          </Button>
        </Paper>
      )}

      {isLoading ? <CircularProgress size={24} /> : (
        <TableContainer component={Paper} variant="outlined" data-testid="investigation-list">
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Customer', 'Agent', 'Status', 'Priority', 'Finding', 'Calls', 'Received', ''].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No investigations yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r: any) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.customer_name || '—'}</TableCell>
                  <TableCell>{r.employee_name || '—'}</TableCell>
                  <TableCell><Chip size="small" label={r.status} /></TableCell>
                  <TableCell>{r.priority}</TableCell>
                  <TableCell>{r.finding || '—'}</TableCell>
                  <TableCell>{r.linked_call_count}</TableCell>
                  <TableCell>{r.complaint_received_at ? new Date(r.complaint_received_at).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/call-qa/investigations/${r.id}`)}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export const CallQaInvestigationDetailPage: React.FC = () => {
  const { investigationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('call_qa.investigations.read');
  const canManage = permissions.includes('call_qa.investigations.manage');
  const canCoach = permissions.includes('call_qa.coaching.manage');
  const queryClient = useQueryClient();
  const [linkCallId, setLinkCallId] = useState('');
  const [evCallId, setEvCallId] = useState('');
  const [evStart, setEvStart] = useState('0');
  const [evEnd, setEvEnd] = useState('30000');
  const [notes, setNotes] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['callQaInvestigation', investigationId],
    queryFn: () => apiClient.callQa.getInvestigation(String(investigationId)),
    enabled: canRead && Boolean(investigationId),
  });

  const inv = data?.data?.data;

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiClient.callQa.updateInvestigation(String(investigationId), payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaInvestigation', investigationId] }),
  });
  const linkMutation = useMutation({
    mutationFn: () => apiClient.callQa.linkInvestigationCall(String(investigationId), { call_id: linkCallId }),
    onSuccess: () => {
      setLinkCallId('');
      queryClient.invalidateQueries({ queryKey: ['callQaInvestigation', investigationId] });
    },
  });
  const unlinkMutation = useMutation({
    mutationFn: (callId: string) => apiClient.callQa.unlinkInvestigationCall(String(investigationId), callId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaInvestigation', investigationId] }),
  });
  const evidenceMutation = useMutation({
    mutationFn: () => apiClient.callQa.addInvestigationEvidence(String(investigationId), {
      call_id: evCallId,
      start_ms: Number(evStart),
      end_ms: Number(evEnd),
      label: 'Relevant Evidence',
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaInvestigation', investigationId] }),
  });
  const coachingMutation = useMutation({
    mutationFn: () => apiClient.callQa.createCoaching({
      employee_id: inv?.employee_id,
      campaign_id: inv?.campaign_id,
      investigation_id: inv?.id,
      topic: `Investigation follow-up: ${(inv?.complaint_description || '').slice(0, 80)}`,
      call_ids: (inv?.linked_calls || []).map((c: any) => c.call_id),
      description: `Created from investigation ${inv?.id}`,
    }),
    onSuccess: (res) => {
      const id = res.data?.data?.id;
      if (id) navigate(`/call-qa/coaching/${id}`);
    },
  });

  React.useEffect(() => {
    if (inv?.notes != null) setNotes(String(inv.notes || ''));
  }, [inv?.id]);

  if (!canRead) {
    return <Alert severity="warning">Investigations require call_qa.investigations.read.</Alert>;
  }
  if (isLoading || !inv) return <CircularProgress size={24} />;

  return (
    <Box data-testid="investigation-detail">
      <Button size="small" onClick={() => navigate('/call-qa/investigations')} sx={{ mb: 1 }}>← Investigations</Button>
      <Typography variant="h6" gutterBottom>
        Investigation — {inv.customer_name || 'Customer'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {inv.complaint_description}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Case</Typography>
            <Typography variant="body2">Agent: {inv.employee_name || '—'}</Typography>
            <Typography variant="body2">Campaign: {inv.campaign_name || '—'}</Typography>
            <Typography variant="body2">Phone: {inv.customer_phone || '—'}</Typography>
            <Typography variant="body2">Account: {inv.account_number || '—'}</Typography>
            <Typography variant="body2">Source: {inv.source || '—'}</Typography>
            <Typography variant="body2">Reviewer: {inv.assigned_reviewer_name || '—'}</Typography>

            {canManage && (
              <Box sx={{ mt: 2 }} display="flex" flexDirection="column" gap={1}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={inv.status}
                    data-testid="investigation-status"
                    onChange={(e) => updateMutation.mutate({ status: e.target.value })}
                  >
                    {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Finding</InputLabel>
                  <Select
                    label="Finding"
                    value={inv.finding || ''}
                    data-testid="investigation-finding"
                    onChange={(e) => updateMutation.mutate({ finding: e.target.value || null })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {FINDINGS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    label="Priority"
                    value={inv.priority || 'NORMAL'}
                    onChange={(e) => updateMutation.mutate({ priority: e.target.value })}
                  >
                    {PRIORITIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  label="Reviewer notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <Button size="small" variant="outlined" onClick={() => updateMutation.mutate({ notes })}>
                  Save notes
                </Button>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  label="Follow-up actions"
                  defaultValue={inv.follow_up_actions || ''}
                  onBlur={(e) => updateMutation.mutate({ follow_up_actions: e.target.value })}
                />
                {canCoach && inv.employee_id && (
                  <Button size="small" variant="contained" disabled={coachingMutation.isPending} onClick={() => coachingMutation.mutate()}>
                    Create Coaching Item
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="investigation-linked-calls">
            <Typography variant="subtitle2" gutterBottom>Linked Calls</Typography>
            {(inv.linked_calls || []).map((c: any) => (
              <Box key={c.call_id} display="flex" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Button size="small" onClick={() => navigate(`/call-qa/calls/${c.call_id}`)}>
                  {c.customer_name || c.call_id.slice(0, 8)}
                </Button>
                <Chip size="small" label={c.qa_status || '—'} />
                {c.is_retention_protected && <Chip size="small" color="info" label="Retention protected" />}
                {canManage && (
                  <Button size="small" color="warning" onClick={() => unlinkMutation.mutate(c.call_id)}>Unlink</Button>
                )}
              </Box>
            ))}
            {canManage && (
              <Box display="flex" gap={1} sx={{ mt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Call ID to link"
                  value={linkCallId}
                  onChange={(e) => setLinkCallId(e.target.value)}
                  data-testid="investigation-link-call-id"
                />
                <Button size="small" variant="outlined" disabled={!linkCallId || linkMutation.isPending} onClick={() => linkMutation.mutate()}>
                  Link
                </Button>
              </Box>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }} data-testid="investigation-evidence">
            <Typography variant="subtitle2" gutterBottom>Evidence Bookmarks</Typography>
            <Alert severity="info" sx={{ mb: 1 }}>
              Bookmarks reference the original recording timestamps — audio is not duplicated.
            </Alert>
            {(inv.evidence || []).map((e: any) => (
              <Box key={e.id} sx={{ mb: 1 }}>
                <Button
                  size="small"
                  onClick={() => navigate(`/call-qa/calls/${e.call_id}?seek_ms=${e.start_ms}`)}
                  data-testid={`investigation-evidence-${e.id}`}
                >
                  {e.label || 'Evidence'} {formatMs(e.start_ms)}–{formatMs(e.end_ms)}
                </Button>
              </Box>
            ))}
            {canManage && (
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                <TextField size="small" label="Call ID" value={evCallId} onChange={(e) => setEvCallId(e.target.value)} />
                <TextField size="small" label="Start ms" value={evStart} onChange={(e) => setEvStart(e.target.value)} sx={{ width: 100 }} />
                <TextField size="small" label="End ms" value={evEnd} onChange={(e) => setEvEnd(e.target.value)} sx={{ width: 100 }} />
                <Button size="small" variant="outlined" disabled={!evCallId || evidenceMutation.isPending} onClick={() => evidenceMutation.mutate()}>
                  Add evidence
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CallQaInvestigationsPage;
